import React from 'react';
import { InlineMath } from 'react-katex';

const convertExponentSlashFractions = (mathText) => {
  if (!mathText) return mathText;

  return mathText.replace(/\^\{([^{}]+)\}/g, (match, content) => {
    if (!content.includes('/') || content.includes('\\frac')) return match;

    let converted = content.replace(
      /\(([^/)]+)\/([^)]+)\)/g,
      (slashMatch, numerator, denominator) =>
        `\\frac{${numerator}}{${denominator}}`
    );

    converted = converted.replace(
      /([0-9a-zA-Z]+)\s*\/\s*([0-9a-zA-Z]+)/g,
      (slashMatch, numerator, denominator) => {
        if (slashMatch.includes('\\frac')) return slashMatch;
        return `\\frac{${numerator}}{${denominator}}`;
      }
    );

    return `^{${converted}}`;
  });
};

export const convertDivisionToFraction = (mathText) => {
  if (!mathText) return mathText;

  let result = convertExponentSlashFractions(mathText);

  // Handle parenthesized numerator: (content)/denominator
  // Skip when '(' follows alphanumeric (e.g. n(n+1)/2 → \frac{n(n+1)}{2}, not n\frac{n+1}{2})
  result = result.replace(
    /(?<![a-zA-Z0-9])\(([^)]+)\)\s*\/\s*([^\s()]+)/g,
    (match, numerator, denominator) => {
      const escapedNumerator = numerator.trim().replace(/\$/g, '\\$');
      const escapedDenominator = denominator.trim().replace(/\$/g, '\\$');
      return `\\frac{${escapedNumerator}}{${escapedDenominator}}`;
    }
  );

  result = result.replace(
    /\(([^/)]+)\/([^)]+)\)/g,
    (match, numerator, denominator) => {
      const escapedNumerator = numerator.trim().replace(/\$/g, '\\$');
      const escapedDenominator = denominator.trim().replace(/\$/g, '\\$');
      return `\\frac{${escapedNumerator}}{${escapedDenominator}}`;
    }
  );

  result = result.replace(
    /([^\/\s\\]+)\s*\/\s*([^\/\s]+)/g,
    (match, numerator, denominator) => {
      if (match.includes('\\frac')) return match;
      // Skip when slash is inside an exponent/group, e.g. a^{m/n} → not \frac{a^{m}{n}}
      if (numerator.includes('{') || denominator.includes('}')) return match;

      const escapedNumerator = numerator.replace(/\$/g, '\\$');
      const escapedDenominator = denominator.replace(/\$/g, '\\$');
      return `\\frac{${escapedNumerator}}{${escapedDenominator}}`;
    }
  );

  return result;
};

export const convertSqrtToLatex = (mathText) => {
  if (!mathText) return mathText;
  return mathText.replace(/sqrt\(([^)]+)\)/g, '\\sqrt{$1}');
};

export const convertCaretExponentsToLatex = (mathText) => {
  if (!mathText) return mathText;

  let result = mathText.replace(/\^\(([^)]+)\)/g, '^{$1}');

  result = result.replace(
    /\^(-?[0-9]+|[A-Za-z])(?![{(\w])/g,
    '^{$1}'
  );

  return result;
};

const MATH_SYMBOL_PATTERN = /[\^²³π√∞≤≥≠±∑∏∫∂∆×÷\\_{}]/;

export const isNaturalLanguageText = (text) => {
  if (!text || typeof text !== 'string') return false;
  if (!/\s/.test(text)) return false;
  if (/\\[a-zA-Z]+/.test(text)) return false;
  return /\b[a-zA-Z]{3,}\b/.test(text);
};

const stripTrailingPunct = (token) => {
  const match = token.match(/([,.:;!?]+)$/);
  if (!match) return { core: token, punct: '' };
  return { core: token.slice(0, -match[1].length), punct: match[1] };
};

export const isMathToken = (token) => {
  if (!token) return false;

  const { core } = stripTrailingPunct(token);
  if (!core) return false;

  if (/^[a-zA-Z]{3,}$/.test(core)) return false;
  if (/^[0-9a-zA-Z]+(?:-[a-zA-Z0-9]+)+$/.test(core)) return false;

  if (MATH_SYMBOL_PATTERN.test(core)) return true;
  if (/\\[a-zA-Z]+/.test(core)) return true;
  if (/\//.test(core)) return true;
  if (/[+\-*=<>]/.test(core)) return true;
  if (/^[a-zA-Z0-9]*\([^)]+\)[a-zA-Z0-9]*/.test(core)) return true;

  return false;
};

const restoreCurrencyAndPercent = (value) =>
  value.replace(/§CURRENCY§/g, '$').replace(/§PERCENTAGE§/g, '%');

const prepareMathForKatex = (mathText) => {
  let convertedMath = convertSqrtToLatex(mathText);
  convertedMath = convertDivisionToFraction(convertedMath);
  convertedMath = convertCaretExponentsToLatex(convertedMath);
  return convertedMath
    .replace(/×/g, '\\times')
    .replace(/÷/g, '\\div')
    .replace(/§CURRENCY§/g, '\\$')
    .replace(/§PERCENTAGE§/g, '\\%');
};

const renderMathToken = (token, index) => {
  const { core, punct } = stripTrailingPunct(token);

  if (!isMathToken(token)) {
    return <span key={index}>{restoreCurrencyAndPercent(token)}</span>;
  }

  try {
    return (
      <span key={index}>
        <InlineMath math={prepareMathForKatex(core)} />
        {punct}
      </span>
    );
  } catch (error) {
    console.warn('KaTeX parsing failed for math part:', error);
    return <span key={index}>{restoreCurrencyAndPercent(token)}</span>;
  }
};

export const parseMixedMathContent = (content) => {
  if (!content) return content;

  let processedContent = content
    .replace(/\$/g, '§CURRENCY§')
    .replace(/%/g, '§PERCENTAGE§');

  const isComplexMath =
    /[+\-*/^()=<>→²³√∞≤≥≠±∑∏∫∂∆_{}]/.test(processedContent) ||
    /\\/.test(processedContent) ||
    /\//.test(processedContent) ||
    /sqrt\(/.test(processedContent) ||
    /\\[a-zA-Z]+/.test(processedContent);

  if (isComplexMath && !isNaturalLanguageText(processedContent)) {
    try {
      return <InlineMath math={prepareMathForKatex(processedContent)} />;
    } catch (error) {
      console.warn('KaTeX parsing failed for complex math:', error);
      return content;
    }
  }

  const parts = processedContent.split(/(\s+)/);
  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null;

        if (/^\s+$/.test(part)) {
          return <span key={index}>{part}</span>;
        }

        return renderMathToken(part, index);
      })}
    </>
  );
};

const QUESTION_MARK_MATH = /\?([^?]{1,80})\?/g;

const looksLikeQuestionMarkMath = (inner) => {
  const trimmed = inner.trim();
  if (!trimmed || !/\d/.test(trimmed)) return false;
  if (/\b[a-zA-Z]{3,}\b/.test(trimmed)) return false;
  return true;
};

/** Some quant items wrap math in ?... ? instead of §...§. */
export const normalizeQuestionMarkMath = (text) => {
  if (!text || !text.includes('?')) return text;

  return text.replace(QUESTION_MARK_MATH, (match, inner) =>
    looksLikeQuestionMarkMath(inner) ? `§${inner.trim()}§` : match
  );
};

export const renderGreMathText = (text) => {
  if (!text) return text;

  text = normalizeQuestionMarkMath(text);

  if (text.includes('§')) {
    const parts = text.split('§');

    return (
      <>
        {parts.map((part, index) => {
          if (index % 2 === 1) {
            try {
              return <span key={index}>{parseMixedMathContent(part)}</span>;
            } catch (error) {
              return <span key={index}>{part}</span>;
            }
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  }

  const convertToLatex = (inputText) =>
    convertCaretExponentsToLatex(
      inputText
        .replace(/π/g, '\\pi')
        .replace(/r²/g, 'r^2')
        .replace(/×/g, '\\times')
        .replace(/÷/g, '\\div')
        .replace(/π\(([^)]+)\)²/g, '\\pi($1)^2')
        .replace(/(\([^)]*\)|[^\/\s]+)\s*\/\s*([^\/\s]+)/g, '\\frac{$1}{$2}')
    );

  const mathPattern = /[\^²π√∞≤≥≠±∑∏∫∂∆]/;
  const hasSpecialChars = mathPattern.test(text);
  const hasExponents = /\^/.test(text);
  const hasFractions = /\//.test(text) && /\d/.test(text);

  if (hasSpecialChars || hasExponents || hasFractions) {
    if (isNaturalLanguageText(text)) {
      return parseMixedMathContent(text);
    }

    const latexText = convertToLatex(text);
    try {
      return <InlineMath math={latexText} />;
    } catch (error) {
      return text;
    }
  }

  return text;
};
